import {
  SelectOptionOld,
  useModalOldContext,
  WSButtons,
  WSCentered,
  WSContainer,
  WSFlexBox,
  WSFormOld,
  WSPanel,
  WSSelectOld,
  WSTable,
  WSTableCell,
  WSText
} from "@wingspanhq/fe-component-library";
import { PayerUpdate } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { payersApi } from "@api";
import { queriesPayers } from "@query";
import React, { useState } from "react";
import { Redirect, RouteComponentProps } from "react-router-dom";
import { ProjectOnboardingLayout } from "../../../../components/ProjectOnboardingLayout/ProjectOnboardingLayout";
import { ErrorAlert } from "@components/ErrorAlert";
import { selectQuickbooksUserOptions } from "../../../../modules/Integrations/selectors/selectQuickbooksUserOptions";
import { selectorCounterpartyDisplayName } from "../../../../shared/selectors/selectorCounterpartyDisplayName";
import { WSQueries } from "../../../../query/WSQuery";
import {
  useQueryIntegrationsQuickbooks,
  useQueryIntegrationsQuickbooksCustomers
} from "../../../../query/integrations/queries";
import { isQuickbooksAuthenticated } from "../../../../query/integrations/selectors";
import { bulkActionFactory } from "../../../../modules/BulkActions";
import { getChangedData } from "../../../../utils/getChangedData";
import styles from "./Quickbooks.module.scss";
import {
  INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
  QUICKBOOKS_ENTITY,
  RefreshModal
} from "./RefreshButton";

export const QuickbooksSetupStep4: React.FC<RouteComponentProps<any>> = ({
  history
}) => {
  const { openModal } = useModalOldContext();

  const qIntegrationsQuickbooks = useQueryIntegrationsQuickbooks();
  const qIntegrationsQuickbooksCustomers =
    useQueryIntegrationsQuickbooksCustomers();
  // v3 clients — replaces this step's prior v1 payer-row search (all payer rows,
  // no status filter). Provides the v3 payer ids + the current QBO customer
  // mapping; v1/v3 payer ids are disjoint, so both the read and write run on v3.
  const queryPayers = queriesPayers.listPayers.useQueryAll();

  const [error, setError] = useState("");

  const updatePayersAction = bulkActionFactory({
    onStart() {
      setError("");
    },
    async action(data: { clientId: string; customerId: string | null }) {
      // v3: the QBO customer↔client mapping now has a per-payer v3 home,
      // `PayerUpdate.quickbooksCustomerId` (SDK 1.20.0), mirroring the payee
      // `quickbooksVendorId`. QA: confirm the deployed backend persists this and
      // that the QBO sync engine reads the v3-written field (see V3_MIGRATION_TRACKER).
      const payerUpdate: PayerUpdate = {};
      // A cleared select un-maps the client (v1 parity — sends null); PayerUpdate
      // types the field as `string`, so widen once at this boundary.
      (
        payerUpdate as { quickbooksCustomerId?: string | null }
      ).quickbooksCustomerId = data.customerId;
      await payersApi.updatePayer({ payerId: data.clientId, payerUpdate });
    },
    onFinish({ failedItems }) {
      if (failedItems.length) {
        setError("Something went wrong!");
      } else {
        // The direct SDK write above doesn't auto-invalidate; refresh the v3
        // client list so Step 5's dedup reflects the mappings just written.
        void WSQueryCache.invalidateQueries(
          queriesPayers.listPayers.queryKeyBase
        );
        history.push("/member/settings/integrations/quickbooks/setup/step/5");
      }
    }
  });

  return (
    <ProjectOnboardingLayout progress={(100 / 8) * 4}>
      <WSQueries
        queries={{
          qIntegrationsQuickbooks,
          qIntegrationsQuickbooksCustomers
        }}
      >
        {({
          qIntegrationsQuickbooks,
          qIntegrationsQuickbooksCustomersData
        }) => {
          const integrationState = qIntegrationsQuickbooks.data;

          const quickbooksCustomerOptions: SelectOptionOld[] =
            selectQuickbooksUserOptions(qIntegrationsQuickbooksCustomersData);

          if (!isQuickbooksAuthenticated(integrationState)) {
            return (
              <Redirect to="/member/settings/integrations/quickbooks/setup" />
            );
          }

          // Wait for the client list before mounting the form — WSFormOld seeds
          // its default values once, on mount.
          if (queryPayers.isInitialLoading) {
            return null;
          }

          const payers = queryPayers.flatData ?? [];

          const defaultFormData = payers.reduce(
            (res, payer) => ({
              ...res,
              [payer.id!]: payer.quickbooksCustomerId ?? undefined
            }),
            {} as any
          );

          return (
            <WSContainer verticalPadding>
              <WSCentered span={{ m: "8" }}>
                <WSText.ParagraphSm color="gray500" mt="M" pt="M">
                  4 of 8
                </WSText.ParagraphSm>
                <WSText.Heading4>Map Customers</WSText.Heading4>

                <WSPanel mt="2XL" mb="5XL">
                  <WSFormOld
                    onSubmit={async formData => {
                      const changedData = getChangedData(
                        defaultFormData,
                        formData
                      );

                      await updatePayersAction.run(
                        Object.keys(changedData).map(clientId => ({
                          clientId,
                          customerId: changedData[clientId] ?? null
                        }))
                      );
                    }}
                    defaultValues={defaultFormData}
                  >
                    <WSText.Heading5 mt="XL">
                      Import your QuickBooks customers and associate them to
                      Wingspan clients
                    </WSText.Heading5>
                    {payers.length > 0 ? (
                      <>
                        <WSText mt="XL">
                          Please map your existing clients to customers in
                          QuickBooks. Any clients you don't map will have a new
                          customer created in QBO alongside any invoicing
                          activity
                        </WSText>

                        <WSTable
                          mt="XL"
                          tableData={payers.map(payer => ({
                            id: payer.id!,
                            data: payer
                          }))}
                          columns={[
                            {
                              config: {
                                gridTemplateSizeMax: "1fr"
                              },
                              renderFunction: ({ data }) => {
                                const name =
                                  selectorCounterpartyDisplayName(data);
                                return (
                                  <WSTableCell
                                    avatar={{
                                      type: "text",
                                      text: name
                                    }}
                                    text={name}
                                    secondaryText={data.email}
                                  />
                                );
                              }
                            },
                            {
                              config: {
                                gridTemplateSizeMax: "1fr",
                                className: styles.column
                              },
                              renderFunction: ({ data }) => (
                                <WSFlexBox.CenterY wrap="nowrap">
                                  <WSFormOld.Field
                                    className={styles.select}
                                    name={data.id!}
                                    component={WSSelectOld}
                                    componentProps={{
                                      placeholder: "Not mapped",
                                      options: quickbooksCustomerOptions,
                                      searchable: true,
                                      cleanable: true,
                                      placeholderActions: [
                                        {
                                          label: "Resync QBO Customers",
                                          icon: "refresh-v",
                                          callback() {
                                            openModal(
                                              INTEGRATIONS_QUICKBOOKS_RESYNC_SELECT,
                                              {
                                                entity:
                                                  QUICKBOOKS_ENTITY.CUSTOMERS
                                              }
                                            );
                                          }
                                        }
                                      ]
                                    }}
                                    defaultValue={
                                      data.quickbooksCustomerId ?? undefined
                                    }
                                    label=""
                                  />
                                </WSFlexBox.CenterY>
                              )
                            }
                          ]}
                        />

                        <ErrorAlert
                          mt="2XL"
                          context="IntegrationsQuickbooksMapping"
                          error={error}
                        />
                      </>
                    ) : (
                      <>
                        <WSText mt="XL">
                          It appears you have no existing clients in Wingspan.
                          You can skip this step.
                        </WSText>
                      </>
                    )}
                    <WSFlexBox.Center mt="3XL">
                      <WSButtons>
                        <WSFormOld.SubmitButton name="next">
                          Next
                        </WSFormOld.SubmitButton>
                      </WSButtons>
                    </WSFlexBox.Center>
                  </WSFormOld>
                </WSPanel>
              </WSCentered>
            </WSContainer>
          );
        }}
      </WSQueries>
      <RefreshModal />
    </ProjectOnboardingLayout>
  );
};
